import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, rateSchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { taxRateCodeSchema } from "../masterdata/tax.js";
export { DEFAULT_EXPENSE_CATEGORIES } from "./defaults.js";
/**
 * Gider yönetimi (Prompt 11 §5; özellik bayrağı `EXPENSES_BUDGET`). Ayrıntı: docs/domain/expenses.md
 *
 * Değişmezler:
 *  · Gider ya TEK istasyona / iş birimine ya da bir DAĞITIM ANAHTARINA yazılır; paylar ONAY anında hesaplanıp
 *    dondurulur (anahtar sonradan değişse de kesilmiş fiş değişmez).
 *  · Belge numarası (`GD{yıl}{6 hane}`) yalnızca onayda alınır — taslak numara tüketmez.
 *  · Onaylanmış gider değiştirilemez; düzeltme İPTALdir (ters fiş, açık kalem VOID, hazine iptali).
 */
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
const reasonSchema = z.string().trim().min(3).max(500);
const positiveMoneySchema = moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır");
const queryBoolean = z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true");
// ── Sabitler ve Türkçe etiketler ─────────────────────────────────────────────
export const EXPENSE_DOC_KINDS = [
    "INCOMING_INVOICE",
    "RECEIPT",
    "EXPENSE_VOUCHER",
    "SELF_EMPLOYED_RECEIPT",
    "BANK_SLIP",
];
export const expenseDocKindSchema = z.enum(EXPENSE_DOC_KINDS);
export const EXPENSE_DOC_KIND_LABELS = {
    INCOMING_INVOICE: "Fatura",
    RECEIPT: "Fiş / makbuz",
    EXPENSE_VOUCHER: "Gider pusulası",
    SELF_EMPLOYED_RECEIPT: "Serbest meslek makbuzu",
    BANK_SLIP: "Dekont",
};
export const EXPENSE_PAYMENT_METHODS = ["CASH", "BANK", "COMPANY_CARD", "ON_ACCOUNT"];
export const expensePaymentMethodSchema = z.enum(EXPENSE_PAYMENT_METHODS);
export const EXPENSE_PAYMENT_METHOD_LABELS = {
    CASH: "Kasa",
    BANK: "Banka",
    COMPANY_CARD: "Şirket kartı",
    ON_ACCOUNT: "Cariye borç",
};
export const EXPENSE_STATUSES = ["DRAFT", "PENDING_APPROVAL", "APPROVED", "REJECTED", "CANCELLED"];
export const expenseStatusSchema = z.enum(EXPENSE_STATUSES);
export const EXPENSE_STATUS_LABELS = {
    DRAFT: "Taslak",
    PENDING_APPROVAL: "Onay bekliyor",
    APPROVED: "Onaylandı",
    REJECTED: "Reddedildi",
    CANCELLED: "İptal edildi",
};
export const expenseLedgerStatusSchema = z.enum(["NONE", "POSTED", "QUEUED", "REVERSED"]);
export const EXPENSE_LEDGER_STATUS_LABELS = {
    NONE: "Fiş üretmedi",
    POSTED: "Muhasebeleşti",
    QUEUED: "Eşleştirme bekliyor",
    REVERSED: "Ters kayıt alındı",
};
export const ALLOCATION_METHODS = ["RATIO", "AREA_M2", "REVENUE", "FIXED"];
export const allocationMethodSchema = z.enum(ALLOCATION_METHODS);
export const ALLOCATION_METHOD_LABELS = {
    RATIO: "Orana göre",
    AREA_M2: "Alana göre (m²)",
    REVENUE: "Ciroya göre",
    FIXED: "Sabit tutar",
};
export const UTILITY_METER_TYPES = ["ELECTRICITY", "WATER", "NATURAL_GAS"];
export const utilityMeterTypeSchema = z.enum(UTILITY_METER_TYPES);
export const UTILITY_METER_TYPE_LABELS = {
    ELECTRICITY: "Elektrik",
    WATER: "Su",
    NATURAL_GAS: "Doğalgaz",
};
export const utilityMeterUnitSchema = z.enum(["KWH", "M3"]);
export const UTILITY_METER_UNIT_LABELS = {
    KWH: "kWh",
    M3: "m³",
};
export const RECURRENCE_FREQUENCIES = ["MONTHLY", "QUARTERLY", "SEMIANNUAL", "YEARLY"];
export const recurrenceFrequencySchema = z.enum(RECURRENCE_FREQUENCIES);
export const RECURRENCE_FREQUENCY_LABELS = {
    MONTHLY: "Aylık",
    QUARTERLY: "Üç ayda bir",
    SEMIANNUAL: "Altı ayda bir",
    YEARLY: "Yıllık",
};
export const recurringAmountModeSchema = z.enum(["FIXED", "LAST"]);
export const RECURRING_AMOUNT_MODE_LABELS = {
    FIXED: "Sabit tutar",
    LAST: "Son onaylanan tutar",
};
/** `EXPENSE:{kod}` kayıt anahtarının parametresi olacağı için desen kayıt anahtarı parametresiyle uyumludur. */
export const expenseCategoryCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Kod büyük harf, rakam ve _ içerebilir (ör. BAKIM_ONARIM)");
/** Tekrarlayan giderin dönem anahtarı: YYYY-AA. */
export const expensePeriodKeySchema = z.string().regex(/^\d{4}-\d{2}$/, "Dönem anahtarı YYYY-AA olmalıdır");
// ── Gider kategorileri (ağaç) ────────────────────────────────────────────────
export const expenseCategorySchema = z.object({
    id: uuidSchema,
    parentId: uuidSchema.nullable(),
    code: z.string(),
    name: z.string(),
    defaultVatRateCode: z.string().nullable(),
    isUtility: z.boolean().describe("Sayaçlı gider: belgede sayaç ve tüketim istenir"),
    sortOrder: z.number().int(),
    isActive: z.boolean(),
    /** Bu kategoriye yazılmış gider var mı (silinemez → pasifleştirilir). */
    hasExpenses: z.boolean(),
    childCount: z.number().int(),
    version: versionSchema,
});
export const expenseCategoryListSchema = z.object({ items: z.array(expenseCategorySchema) });
export const listExpenseCategoriesQuerySchema = z.object({
    includeInactive: queryBoolean,
    onlyUtility: queryBoolean,
});
export const createExpenseCategorySchema = z.object({
    code: expenseCategoryCodeSchema,
    name: z.string().trim().min(1).max(150),
    parentId: uuidSchema.nullable().default(null),
    defaultVatRateCode: taxRateCodeSchema.nullable().default(null),
    isUtility: z.boolean().default(false),
    sortOrder: z.number().int().min(0).max(9999).default(0),
});
/** Kod DEĞİŞMEZ (kayıt anahtarı parametresidir); gönderilmeyen alan güncellenmez. */
export const updateExpenseCategorySchema = z.object({
    name: z.string().trim().min(1).max(150).optional(),
    parentId: uuidSchema.nullable().optional(),
    defaultVatRateCode: taxRateCodeSchema.nullable().optional(),
    isUtility: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
});
// ── Dağıtım anahtarları ──────────────────────────────────────────────────────
export const allocationKeyLineSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string().nullable(),
    businessUnitType: businessUnitTypeSchema.nullable(),
    weight: z.string().nullable().describe("RATIO ağırlığı; AREA_M2 / REVENUE'da hesaplanır"),
    fixedAmount: moneySchema.nullable().describe("FIXED: kuruş"),
    takesRemainder: z.boolean(),
});
export const allocationKeySchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    name: z.string(),
    method: allocationMethodSchema,
    revenueMonths: z.number().int(),
    description: z.string().nullable(),
    isActive: z.boolean(),
    lines: z.array(allocationKeyLineSchema),
    version: versionSchema,
});
export const allocationKeyListSchema = z.object({ items: z.array(allocationKeySchema) });
export const listAllocationKeysQuerySchema = z.object({
    companyId: uuidSchema,
    includeInactive: queryBoolean,
});
const allocationKeyLineInputSchema = z.object({
    stationId: uuidSchema,
    businessUnitType: businessUnitTypeSchema.nullable().default(null),
    weight: z
        .string()
        .regex(/^\d{1,14}(\.\d{1,4})?$/, "Ağırlık negatif olmayan bir sayı dizgesi olmalıdır")
        .nullable()
        .default(null),
    fixedAmount: moneySchema.nullable().default(null),
    takesRemainder: z.boolean().default(false),
});
const allocationKeyBodyShape = {
    name: z.string().trim().min(1).max(150),
    method: allocationMethodSchema,
    revenueMonths: z.number().int().min(1).max(24).default(3),
    description: z.string().trim().max(500).nullable().default(null),
    isActive: z.boolean().default(true),
    lines: z.array(allocationKeyLineInputSchema).min(1).max(200),
};
const refineAllocationKeyBody = (schema) => schema
    .refine((value) => value.lines.filter((line) => line.takesRemainder).length <= 1, {
    path: ["lines"],
    message: "Kalan tutarı en çok bir hedef alabilir",
})
    .refine((value) => value.method !== "FIXED" || value.lines.every((line) => line.fixedAmount !== null), {
    path: ["lines"],
    message: "Sabit dağıtımda her hedefe bir tutar girilmelidir",
})
    .refine((value) => value.method !== "RATIO" || value.lines.every((line) => line.weight !== null), {
    path: ["lines"],
    message: "Orana göre dağıtımda her hedefe bir ağırlık girilmelidir",
});
export const createAllocationKeySchema = refineAllocationKeyBody(z.object({ companyId: uuidSchema, ...allocationKeyBodyShape }));
/** PUT: anahtarın TAMAMI (satırları dâhil) değiştirilir — kısmi güncelleme yoktur. */
export const saveAllocationKeySchema = refineAllocationKeyBody(z.object({ ...allocationKeyBodyShape, version: versionSchema }));
export const allocationPreviewQuerySchema = z.object({
    amount: positiveMoneySchema.describe("Dağıtılacak matrah (kuruş)"),
    asOf: localDateSchema.optional().describe("REVENUE / AREA_M2 ağırlıklarının okunacağı tarih"),
});
export const allocationShareSchema = z.object({
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    businessUnitType: businessUnitTypeSchema.nullable(),
    weight: z.string().nullable().describe("Hesaplanan ağırlık (m², ciro ya da oran)"),
    amount: moneySchema,
});
export const allocationPreviewSchema = z.object({
    method: allocationMethodSchema,
    amount: moneySchema,
    shares: z.array(allocationShareSchema),
});
// ── Sayaçlar ve okumalar ─────────────────────────────────────────────────────
export const utilityMeterSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    parentMeterId: uuidSchema.nullable(),
    meterType: utilityMeterTypeSchema,
    businessUnitType: businessUnitTypeSchema.nullable(),
    name: z.string(),
    meterNo: z.string().nullable(),
    unit: utilityMeterUnitSchema,
    isActive: z.boolean(),
    lastReadingDate: localDateSchema.nullable(),
    lastReadingValue: quantitySchema.nullable(),
    version: versionSchema,
});
export const utilityMeterListSchema = z.object({ items: z.array(utilityMeterSchema) });
export const listUtilityMetersQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    meterType: utilityMeterTypeSchema.optional(),
    includeInactive: queryBoolean,
});
export const createUtilityMeterSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema,
    parentMeterId: uuidSchema.nullable().default(null),
    meterType: utilityMeterTypeSchema,
    businessUnitType: businessUnitTypeSchema.nullable().default(null),
    name: z.string().trim().min(1).max(150),
    meterNo: z.string().trim().max(60).nullable().default(null),
    unit: utilityMeterUnitSchema,
});
export const updateUtilityMeterSchema = z.object({
    name: z.string().trim().min(1).max(150).optional(),
    parentMeterId: uuidSchema.nullable().optional(),
    businessUnitType: businessUnitTypeSchema.nullable().optional(),
    meterNo: z.string().trim().max(60).nullable().optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
});
export const utilityReadingSchema = z.object({
    id: uuidSchema,
    meterId: uuidSchema,
    readingDate: localDateSchema,
    readingValue: quantitySchema,
    /** Bir önceki okumaya göre tüketim; ilk okumada null. */
    consumption: quantitySchema.nullable(),
    note: z.string().nullable(),
    createdAt: timestampSchema,
});
export const utilityReadingListSchema = z.object({ items: z.array(utilityReadingSchema) });
export const createUtilityReadingSchema = z.object({
    readingDate: localDateSchema,
    readingValue: quantitySchema,
    note: z.string().trim().max(300).nullable().default(null),
});
export const listUtilityReadingsQuerySchema = z.object({
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
/** Birim maliyet raporu: dönem bazında tüketim, matrah ve birim maliyet (18–19 modüllerinin kaynağı). */
export const utilityUnitCostQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    meterType: utilityMeterTypeSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
export const utilityUnitCostRowSchema = z.object({
    meterId: uuidSchema.nullable(),
    meterName: z.string().nullable(),
    meterType: utilityMeterTypeSchema,
    unit: utilityMeterUnitSchema,
    stationId: uuidSchema.nullable(),
    periodFrom: localDateSchema,
    periodTo: localDateSchema,
    consumption: quantitySchema,
    /** KDV hariç tutar (kuruş). */
    amount: moneySchema,
    /** Birim başına maliyet (TL, 4 ondalık); tüketim sıfırsa null. */
    unitCost: z.string().nullable(),
});
export const utilityUnitCostReportSchema = z.object({ items: z.array(utilityUnitCostRowSchema) });
// ── Şirket kartları ──────────────────────────────────────────────────────────
export const companyCardSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    name: z.string(),
    bankId: uuidSchema.nullable(),
    bankName: z.string().nullable(),
    /** Kart numarasının TAMAMI hiçbir zaman alınmaz ve saklanmaz. */
    lastFour: z.string().nullable(),
    holderName: z.string().nullable().describe("KVKK: kişisel veri"),
    statementDay: z.number().int().nullable(),
    dueDay: z.number().int().nullable(),
    paymentBankAccountId: uuidSchema.nullable(),
    isActive: z.boolean(),
    version: versionSchema,
});
export const companyCardListSchema = z.object({ items: z.array(companyCardSchema) });
export const listCompanyCardsQuerySchema = z.object({
    companyId: uuidSchema,
    includeInactive: queryBoolean,
});
const lastFourSchema = z
    .string()
    .trim()
    .regex(/^\d{4}$/, "Yalnızca kart numarasının SON 4 hanesi kaydedilir");
export const createCompanyCardSchema = z.object({
    companyId: uuidSchema,
    name: z.string().trim().min(1).max(100),
    bankId: uuidSchema.nullable().default(null),
    lastFour: lastFourSchema.nullable().default(null),
    holderName: z.string().trim().max(150).nullable().default(null),
    statementDay: z.number().int().min(1).max(31).nullable().default(null),
    dueDay: z.number().int().min(1).max(31).nullable().default(null),
    paymentBankAccountId: uuidSchema.nullable().default(null),
});
export const updateCompanyCardSchema = z.object({
    name: z.string().trim().min(1).max(100).optional(),
    bankId: uuidSchema.nullable().optional(),
    lastFour: lastFourSchema.nullable().optional(),
    holderName: z.string().trim().max(150).nullable().optional(),
    statementDay: z.number().int().min(1).max(31).nullable().optional(),
    dueDay: z.number().int().min(1).max(31).nullable().optional(),
    paymentBankAccountId: uuidSchema.nullable().optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
});
// ── Tekrarlayan giderler ─────────────────────────────────────────────────────
export const recurringExpenseSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    name: z.string(),
    categoryId: uuidSchema,
    categoryCode: z.string(),
    counterpartyId: uuidSchema.nullable(),
    stationId: uuidSchema.nullable(),
    businessUnitType: businessUnitTypeSchema.nullable(),
    allocationKeyId: uuidSchema.nullable(),
    amountMode: recurringAmountModeSchema,
    netAmount: moneySchema,
    vatRateCode: z.string(),
    withholdingRateCode: z.string().nullable(),
    docKind: expenseDocKindSchema,
    paymentMethod: expensePaymentMethodSchema,
    frequency: recurrenceFrequencySchema,
    dayOfMonth: z.number().int(),
    startDate: localDateSchema,
    endDate: localDateSchema.nullable(),
    leadDays: z.number().int(),
    isActive: z.boolean(),
    /** Bir sonraki vade (üretilmemiş ilk dönem); bitmiş tanımda null. */
    nextDueDate: localDateSchema.nullable(),
    version: versionSchema,
});
export const recurringExpenseListSchema = z.object({ items: z.array(recurringExpenseSchema) });
export const listRecurringExpensesQuerySchema = z.object({
    companyId: uuidSchema,
    includeInactive: queryBoolean,
});
const recurringBodyShape = {
    name: z.string().trim().min(1).max(150),
    categoryId: uuidSchema,
    counterpartyId: uuidSchema.nullable().default(null),
    stationId: uuidSchema.nullable().default(null),
    businessUnitType: businessUnitTypeSchema.nullable().default(null),
    allocationKeyId: uuidSchema.nullable().default(null),
    amountMode: recurringAmountModeSchema.default("FIXED"),
    netAmount: positiveMoneySchema,
    vatRateCode: taxRateCodeSchema,
    withholdingRateCode: taxRateCodeSchema.nullable().default(null),
    docKind: expenseDocKindSchema.default("INCOMING_INVOICE"),
    paymentMethod: expensePaymentMethodSchema.default("ON_ACCOUNT"),
    frequency: recurrenceFrequencySchema,
    dayOfMonth: z.number().int().min(1).max(31),
    startDate: localDateSchema,
    endDate: localDateSchema.nullable().default(null),
    leadDays: z.number().int().min(0).max(60).default(5),
};
export const createRecurringExpenseSchema = z
    .object({ companyId: uuidSchema, ...recurringBodyShape })
    .refine((value) => value.allocationKeyId === null || value.stationId === null, {
    path: ["allocationKeyId"],
    message: "Dağıtım anahtarı seçilen tanımda istasyon seçilemez",
})
    .refine((value) => value.paymentMethod !== "ON_ACCOUNT" || value.counterpartyId !== null, {
    path: ["counterpartyId"],
    message: "Cariye borç yazılan tanımda cari zorunludur",
})
    .refine((value) => value.endDate === null || value.endDate >= value.startDate, {
    path: ["endDate"],
    message: "Bitiş tarihi başlangıçtan önce olamaz",
});
export const updateRecurringExpenseSchema = z.object({
    name: z.string().trim().min(1).max(150).optional(),
    counterpartyId: uuidSchema.nullable().optional(),
    amountMode: recurringAmountModeSchema.optional(),
    netAmount: positiveMoneySchema.optional(),
    vatRateCode: taxRateCodeSchema.optional(),
    withholdingRateCode: taxRateCodeSchema.nullable().optional(),
    paymentMethod: expensePaymentMethodSchema.optional(),
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    endDate: localDateSchema.nullable().optional(),
    leadDays: z.number().int().min(0).max(60).optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
});
// ── Gider belgesi ────────────────────────────────────────────────────────────
export const expenseAllocationSchema = z.object({
    stationId: uuidSchema.nullable(),
    /** İstasyonun adı (şirket geneli payda null) — arayüz istasyon listesini ayrıca çekmesin diye. */
    stationName: z.string().nullable(),
    businessUnitType: businessUnitTypeSchema.nullable(),
    amount: moneySchema,
});
export const expenseSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema.nullable(),
    businessUnitType: businessUnitTypeSchema.nullable(),
    allocationKeyId: uuidSchema.nullable(),
    /** GD2026000042 — yalnızca onayda alınır. */
    expenseNo: z.string().nullable(),
    docKind: expenseDocKindSchema,
    docNo: z.string().nullable(),
    docDate: localDateSchema,
    dueDate: localDateSchema,
    counterpartyId: uuidSchema.nullable(),
    categoryId: uuidSchema,
    categoryCode: z.string(),
    categoryName: z.string(),
    description: z.string(),
    netAmount: moneySchema,
    vatRateCode: z.string(),
    vatRate: rateSchema,
    vatAmount: moneySchema,
    withholdingRateCode: z.string().nullable(),
    withholdingRate: rateSchema.nullable(),
    withholdingAmount: moneySchema,
    grossAmount: moneySchema,
    payableAmount: moneySchema,
    paymentMethod: expensePaymentMethodSchema,
    cashRegisterId: uuidSchema.nullable(),
    bankAccountId: uuidSchema.nullable(),
    companyCardId: uuidSchema.nullable(),
    fileId: uuidSchema.nullable(),
    recurringExpenseId: uuidSchema.nullable(),
    periodKey: z.string().nullable(),
    invoiceId: uuidSchema.nullable(),
    meterId: uuidSchema.nullable(),
    consumption: quantitySchema.nullable(),
    periodFrom: localDateSchema.nullable(),
    periodTo: localDateSchema.nullable(),
    status: expenseStatusSchema,
    approvalRequestId: uuidSchema.nullable(),
    ledgerStatus: expenseLedgerStatusSchema,
    rejectedReason: z.string().nullable(),
    cancelReason: z.string().nullable(),
    /** Onayda dondurulan paylar (onaylanmamış giderde boş). */
    allocations: z.array(expenseAllocationSchema),
    submittedAt: timestampSchema.nullable(),
    approvedAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    version: versionSchema,
});
export const expensePageSchema = pageOf(expenseSchema);
export const listExpensesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    categoryId: uuidSchema.optional(),
    counterpartyId: uuidSchema.optional(),
    status: expenseStatusSchema.optional(),
    paymentMethod: expensePaymentMethodSchema.optional(),
    dateFrom: localDateSchema.optional(),
    dateTo: localDateSchema.optional(),
    search: z.string().trim().max(100).optional().describe("Açıklama, belge no ya da gider no"),
});
const expenseBodyShape = {
    companyId: uuidSchema,
    stationId: uuidSchema.nullable().default(null),
    businessUnitType: businessUnitTypeSchema.nullable().default(null),
    allocationKeyId: uuidSchema.nullable().default(null),
    docKind: expenseDocKindSchema,
    docNo: z.string().trim().max(60).nullable().default(null),
    docDate: localDateSchema,
    dueDate: localDateSchema.nullable().default(null).describe("Boşsa belge tarihi kullanılır"),
    counterpartyId: uuidSchema.nullable().default(null),
    categoryId: uuidSchema,
    description: z.string().trim().min(1).max(500),
    netAmount: positiveMoneySchema.describe("Matrah (KDV hariç, kuruş)"),
    vatRateCode: taxRateCodeSchema,
    withholdingRateCode: taxRateCodeSchema.nullable().default(null),
    paymentMethod: expensePaymentMethodSchema,
    cashRegisterId: uuidSchema.nullable().default(null),
    bankAccountId: uuidSchema.nullable().default(null),
    companyCardId: uuidSchema.nullable().default(null),
    fileId: uuidSchema.nullable().default(null).describe("Belge görseli (`files`, amaç DOCUMENT)"),
    meterId: uuidSchema.nullable().default(null),
    consumption: quantitySchema.nullable().default(null),
    periodFrom: localDateSchema.nullable().default(null),
    periodTo: localDateSchema.nullable().default(null),
};
const refineExpenseBody = (schema) => schema
    .refine((value) => value.allocationKeyId === null || value.stationId === null, {
    path: ["allocationKeyId"],
    message: "Dağıtım anahtarı seçilen giderde istasyon seçilemez",
})
    .refine((value) => value.paymentMethod !== "ON_ACCOUNT" || value.counterpartyId !== null, {
    path: ["counterpartyId"],
    message: "Cariye borç yazılan giderde cari zorunludur",
})
    .refine((value) => (value.paymentMethod === "CASH") === (value.cashRegisterId !== null), {
    path: ["cashRegisterId"],
    message: "Kasadan ödenen giderde kasa seçilmelidir (başka ödeme biçiminde seçilemez)",
})
    .refine((value) => (value.paymentMethod === "BANK") === (value.bankAccountId !== null), {
    path: ["bankAccountId"],
    message: "Bankadan ödenen giderde banka hesabı seçilmelidir (başka ödeme biçiminde seçilemez)",
})
    .refine((value) => (value.paymentMethod === "COMPANY_CARD") === (value.companyCardId !== null), {
    path: ["companyCardId"],
    message: "Şirket kartıyla ödenen giderde kart seçilmelidir (başka ödeme biçiminde seçilemez)",
})
    .refine((value) => value.dueDate === null || value.dueDate >= value.docDate, {
    path: ["dueDate"],
    message: "Vade belge tarihinden önce olamaz",
})
    .refine((value) => value.periodFrom === null || value.periodTo === null || value.periodTo >= value.periodFrom, { path: ["periodTo"], message: "Dönem bitişi başlangıçtan önce olamaz" });
export const createExpenseSchema = refineExpenseBody(z.object(expenseBodyShape));
/** Önizleme: hiçbir şey yazmaz, numara tüketmez (paylar + fiş satırları). */
export const previewExpenseSchema = createExpenseSchema;
/** TASLAK giderin kısmi güncellemesi; gönderilmeyen alan değişmez. */
export const updateExpenseSchema = z.object({
    stationId: uuidSchema.nullable().optional(),
    businessUnitType: businessUnitTypeSchema.nullable().optional(),
    allocationKeyId: uuidSchema.nullable().optional(),
    docKind: expenseDocKindSchema.optional(),
    docNo: z.string().trim().max(60).nullable().optional(),
    docDate: localDateSchema.optional(),
    dueDate: localDateSchema.nullable().optional(),
    counterpartyId: uuidSchema.nullable().optional(),
    categoryId: uuidSchema.optional(),
    description: z.string().trim().min(1).max(500).optional(),
    netAmount: positiveMoneySchema.optional(),
    vatRateCode: taxRateCodeSchema.optional(),
    withholdingRateCode: taxRateCodeSchema.nullable().optional(),
    paymentMethod: expensePaymentMethodSchema.optional(),
    cashRegisterId: uuidSchema.nullable().optional(),
    bankAccountId: uuidSchema.nullable().optional(),
    companyCardId: uuidSchema.nullable().optional(),
    fileId: uuidSchema.nullable().optional(),
    meterId: uuidSchema.nullable().optional(),
    consumption: quantitySchema.nullable().optional(),
    periodFrom: localDateSchema.nullable().optional(),
    periodTo: localDateSchema.nullable().optional(),
    version: versionSchema,
});
export const submitExpenseSchema = z.object({ version: versionSchema });
export const cancelExpenseSchema = z.object({ reason: reasonSchema, version: versionSchema });
export const expensePostingLineSchema = z.object({
    key: z.string().describe("Anlamsal kayıt anahtarı (ör. EXPENSE:KIRA, VAT_INPUT:VAT_STANDARD)"),
    side: z.enum(["DEBIT", "CREDIT"]),
    amount: moneySchema,
    stationId: uuidSchema.nullable(),
    businessUnitType: businessUnitTypeSchema.nullable(),
    counterpartyId: uuidSchema.nullable(),
    description: z.string().nullable(),
    /** Şirkette eşleştirme çözülüyorsa hesap kodu; yoksa null (kayıt kuyruğa düşer). */
    accountCode: z.string().nullable(),
});
export const expensePreviewSchema = z.object({
    netAmount: moneySchema,
    vatRate: rateSchema,
    vatAmount: moneySchema,
    withholdingRate: rateSchema.nullable(),
    withholdingAmount: moneySchema,
    grossAmount: moneySchema,
    payableAmount: moneySchema,
    allocations: z.array(allocationShareSchema),
    journalLines: z.array(expensePostingLineSchema),
});
/** Tekrarlayan gider işinin sonucu (worker ve el ile tetikleme). */
export const recurringRunResultSchema = z.object({
    created: z.number().int(),
    submitted: z.number().int(),
    /** Taslak açıldı ama onaya GÖNDERİLEMEDİ (kesinleştirme servisleri yok ya da talep edecek kullanıcı yok). */
    notSubmitted: z.array(z.object({
        recurringExpenseId: uuidSchema,
        periodKey: z.string(),
        expenseId: uuidSchema,
        reason: z.string(),
    })),
    /** Kapalı muhasebe dönemine düştüğü için atlanan vadeler. */
    skippedClosedPeriod: z.array(z.object({ recurringExpenseId: uuidSchema, periodKey: z.string(), dueDate: localDateSchema })),
    failed: z.array(z.object({ recurringExpenseId: uuidSchema, error: z.string() })),
});
//# sourceMappingURL=expenses.js.map